<template>
  <main
    id="page-search"
    class="bg-bgr"
    :class="{ 'shadow-inner': !$params.iframe }"
    :data-filters-count="Object.keys(searchState).length"
  >
    <div ref="pageTopAnchor" class="h-0" />

    <header>
      <div class="py-4 px-2 md:py-6" :class="contentWidthClass">
        <div
          class="flex flex-col gap-4 justify-between md:flex-row md:items-center"
        >
          <RedesignSearchBar
            v-model="searchBarState"
            collapsable
            class="md:w-fit"
            @submit="handleSubmit"
          />
          <WishlistLink v-if="!isHeaderEnabled" class="hidden md:flex" />
        </div>
      </div>
      <div v-if="screen.isNotSmallOrUnknown" class="hidden md:block border-b" />
    </header>

    <section
      class="px-2 md:pt-6 grid md:grid-cols-[24rem_1fr] md:gap-6"
      :class="contentWidthClass"
    >
      <aside class="flex flex-col gap-4 md:gap-6">
        <RedesignMapButton
          v-if="$screen.isNotSmallOrUnknown"
          v-model="searchStateModel"
          :disabled="isLoading"
          class="hidden md:block"
        />
        <RedesignFilter
          v-if="screen.isNotSmallOrUnknown"
          v-model="searchStateModel"
          class="hidden md:block border rounded-md"
        />
      </aside>

      <section
        class="sticky-section md:hidden z-5 bg-bgr px-4 py-3 -mx-4 -mt-3"
      >
        <div class="flex justify-between gap-2">
          <div class="grid grid-cols-[auto_auto_1fr] gap-2 items-center">
            <RedesignFilterButton
              v-if="screen.isSmall"
              v-model="searchStateModel"
              class="h-9"
              @on-opened="handleOpenFilterMobile"
            />
            <RedesignMapButton
              v-if="$screen.isSmall && (isLoading || total)"
              v-model="searchStateModel"
              class="h-9"
              :disabled="isLoading"
            />
            <RedesignSearchSorting
              v-if="isLoading || total"
              v-model="searchState.sorting"
              rounded
              mode="right"
              class="min-w-0 h-9"
              :disabled="isLoading"
            />
          </div>
          <WishlistLink v-if="!isHeaderEnabled" minimize="sm" />
        </div>
      </section>

      <section
        ref="searchResultsRef"
        class="grid gap-6 auto-rows-min"
        :aria-busy="isLoading"
        data-id="search-result"
      >
        <template v-if="isLoading || total">
          <RedesignSearchAreaResults
            :items
            :trip
            :loading="isLoading || isFetching"
          >
            <template #title>
              <section class="grid gap-4">
                <div
                  v-if="screen.isSmall"
                  class="block md:hidden border-b -mx-4"
                />
                <SearchResultsHeader :total />
                <SearchResultsHeaderCount :total :loading="isLoading" />
              </section>
            </template>
            <template v-if="$screen.isNotSmallOrUnknown" #toolbar>
              <div
                class="hidden md:grid grid-cols-[auto_1fr] gap-2 items-center"
              >
                <RedesignSearchSorting
                  v-model="searchState.sorting"
                  rounded
                  mode="right"
                  class="min-w-0"
                />
              </div>
            </template>
          </RedesignSearchAreaResults>
          <RedesignSearchPagination
            v-if="!isLoading"
            v-model="currentPage"
            :total="totalPages"
          />
        </template>
        <template v-else>
          <div class="grid gap-4">
            <div
              v-if="screen.isSmall"
              class="block md:hidden border-b -mx-4 mt-0"
            />
            <RedesignSearchAreaNoResults v-model="searchStateModel" />
          </div>
        </template>
      </section>
    </section>

    <SearchRatingBox :key="filtersKey" :filters />

    <SearchTeaser v-if="!$params.iframe" :search-facets="facets">
      <RedesignSearchBreadcrumb :items="breadcrumbs" />
      <template #searchteaser>
        <SearchObjectTeasers
          v-if="objectTeasersEnabled"
          :key="destinationFilterKey"
          :show="objectTeasersVisible"
          :destination="destination"
          :season
        />
      </template>
      <template #contentassets>
        <SearchContentAssets
          v-if="contentAssetsEnabled"
          :key="destinationFilterKey"
          :filter="destinationFilter"
        />
      </template>
    </SearchTeaser>
    <USPCards :why="additionalBody.USPCards.cards" />
    <USPWhy
      :why="[additionalBody.USPColumns.left, additionalBody.USPColumns.right]"
    />
    <FooterNewsletterSignup />
  </main>
</template>

<script setup lang="ts">
import type { DeepReadonly } from 'vue'
import type { SearchBarState } from '~/composables/search.types'

const OBJECT_TEASERS_MIN_RESULTS_TO_DISPLAY = 10
const OBJECT_TEASERS_FILTERS_TO_DISPLAY = new Set([
  'sea',
  'pool',
  'pets',
  'skiing',
  'familyfriendly',
])
const CONTENT_ASSETS_FILTERS_TO_DISPLAY = new Set([
  'sea',
  'pool',
  'pets',
  'familyfriendly',
])

const confData = useConfdata()
const ab = useAB()
const screen = useScreen()
const tracking = useTracking()
const { isHeaderEnabled } = usePageLayout()
const { contentWidthClass } = useContentWidth()
const context = useSearchContext()

const isContentAssetsEnabled = useFlag('content-assets')

const confCurrentRoute = computed(() => confData.currentRoute!)
const initialState = getSearchStateFromRouteConf(context, confCurrentRoute)
if (!initialState)
  throw createError({ statusMessage: 'Search state is not defined' })
const searchState = useSearchState(initialState, handleFilterChange)
const searchStateModel = searchState.model
const searchResults = useSearchResults(searchState)
const {
  key: resultsKey,
  items,
  facets,
  isLoading,
  isFetching,
  total,
  currentPage,
  totalPages,
} = toRefs(searchResults)
const breadcrumbs = useSearchDestinationBreadcrumbs(confCurrentRoute)

const page = computed(() => searchState.page)
const additionalBody = computed(() => confData.additionalBody as SearchPageBody)
const destination = computed(() => confData.destination)
const destinationFilter = computed(() => destination.value?.filters?.[0].key)
const destinationFilterKey = computed(() => destination.value.urlPath)
const route = useRoute()

const searchBarState = ref<SearchBarState>(getSearchBarState(searchState))

function getSearchBarState(state: SearchState) {
  return {
    destination: mapRedesignDestinationToSuggestion(context.destination),
    period: mapDatesStateToDatePeriod(state.dates),
    guests: {
      pax: state.pax ?? PAX_MIN,
      pets: state.minpets || Number(state.pets ?? PETS_MIN),
    },
  }
}

const trip = computed(() => getTripParamsFromState(searchState))

const season = useSearchSeason(trip)

const filters = computed(() => mapSearchStateToSearchApiParams(searchState))

const filtersKey = computed(() => JSON.stringify(searchState))
const objectTeasersEnabled = computed(() =>
  Boolean(
    ab.isActive('objectteaser') &&
      route.path && // handeleing reactivity, see https://jira.migros.net/browse/IHGWEBCC-1382
      confData.isCanonicalPage &&
      (!destinationFilter.value ||
        OBJECT_TEASERS_FILTERS_TO_DISPLAY.has(destinationFilter.value)),
  ),
)
const objectTeasersVisible = computed(
  () =>
    objectTeasersEnabled.value &&
    total.value >= OBJECT_TEASERS_MIN_RESULTS_TO_DISPLAY,
)

const contentAssetsEnabled = computed(() =>
  Boolean(
    isContentAssetsEnabled.value &&
      destination.value?.countryCode &&
      (!destinationFilter.value ||
        CONTENT_ASSETS_FILTERS_TO_DISPLAY.has(destinationFilter.value)),
  ),
)

const applySearchBarState = useSearchBarToSearchStateApply()

async function handleSubmit(submittedState: DeepReadonly<SearchBarState>) {
  const newDestination = submittedState.destination

  if (newDestination && isSuggestionTypeAccom(newDestination.type)) {
    const { fullPath } = useSearchBarRoute(submittedState)
    await navigateTo(fullPath)
  } else {
    const newState = await applySearchBarState(submittedState, searchStateModel)
    searchState.update(newState)
  }
}

const pageTopAnchor = ref<HTMLDivElement>()

function scrollToTopAnchor() {
  pageTopAnchor.value?.scrollIntoView({
    behavior: screen.isSmall ? 'instant' : 'smooth',
  })
}

function handleOpenFilterMobile() {
  tracking.handlers?.redesignFilters.filterMobile(false)
}

watch(page, (pageParamValue) => {
  if (!pageParamValue) return

  scrollToTopAnchor()
})

/**
 * Tracking each new results, even if they match the old ones.
 * For compatibility with the old solution.
 * @see https://jira.migros.net/browse/IHGWEBCC-2327
 */
watch(resultsKey, () => {
  tracking.handlers?.search.results(destination.value, trip.value, total.value)
  tracking.handlers?.ecommerce.viewItemList(items.value)
})

function handleFilterChange(updatedState: SearchState) {
  const searchUrl = generateSearchUrl(context, updatedState)
  navigateTo(withShallowRoute(getRouteFromPath(getUrlFullPath(searchUrl))))
}

onFullRouteUpdate((to) => {
  const routeSearchState = getSearchStateFromRouteConf(context, to)

  if (!routeSearchState) return

  searchState.reset(routeSearchState)
  searchBarState.value = getSearchBarState(routeSearchState)

  scrollToTopAnchor()
})

onMounted(() => {
  window.onbeforeunload = function () {
    // Before a page-reload the browser normally memorises the scroll-position, but on the reloaded searchpage this may have unwanted sideeffects cause of dynamic content.
    // So it is necessary to set the scroll-position to 0 before unload (which leads to position top on reload)
    window.scrollTo(0, 0)
  }
})
</script>

<style scoped>
.sticky-section {
  @apply sticky top-0 shadow-none;

  /* As there is no option to check sticky state, here is a hack to add shadow */
  animation: instant-shadow linear forwards;
  animation-timeline: scroll(root);
  animation-range: 150px 200px; /* Shadow appears between 150px and 200px scroll position */
}

@keyframes instant-shadow {
  0% {
    box-shadow: none;
  }
  100% {
    @apply shadow-lg;
  }
}
</style>
